import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HeaderV2 from '../components/HeaderV2';
import FooterV2 from '../components/FooterV2';
import ArticlePage from './ArticlePage';

/**
 * ArticlePageV2 — 做法 A：完全沿用舊版 ArticlePage 內容與排版，
 * 只替換為 HeaderV2 + FooterV2，並注入 V2 字型風格（EB Garamond + Noto Serif TC）。
 *
 * 路由：/zh|en/lab/:category/v2/:slug（預覽用）
 *      未來可直接掛到 /zh|en/lab/:category/:slug（正式接手）
 * 內容：Hero 大圖 + 標題、文章 HTML、tags、相關文章、分享、Lightbox 全部沿用 ArticlePage。
 * SEO：依 pathname 是否含 '/v2' 動態判斷——
 *   - 預覽路由（含 /v2）：noindex,nofollow + canonical 指舊版單篇 URL，避免重複內容
 *   - 正式路由（不含 /v2）：完全不寫 robots/canonical，讓內嵌 ArticlePage 自帶的 SEO（title/description/og/article meta/canonical/hreflang）生效
 */
const ArticlePageV2: React.FC = () => {
  const location = useLocation();
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const lang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh';
  const isPreviewRoute = location.pathname.includes('/v2');

  // 注入 V2 共用字型（與其他 V2 頁面一致）
  useEffect(() => {
    const id = 'article-v2-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&family=Noto+Serif+TC:wght@400;500&display=swap';
    document.head.appendChild(link);
  }, []);

  const canonicalPath = `/${lang}/lab/${category}/${slug}`;

  return (
    <>
      {isPreviewRoute && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={`https://www.ongucoffee.com${canonicalPath}`} />
        </Helmet>
      )}

      <div className="min-h-screen bg-white">
        <HeaderV2 />
        <ArticlePage />
        <FooterV2 />
      </div>
    </>
  );
};

export default ArticlePageV2;
